import { useEffect, type ReactNode } from 'react';
import { Link, NavLink, useNavigate } from 'react-router';

import { useCurrentUser } from '../data/DataContext.tsx';
import { useAccount } from '../features/account/AccountContext.tsx';
import { useCart } from '../features/cart/CartContext.tsx';
import { useSocialCounts } from '../features/social/counts.ts';
import { GIcon, type IconName } from './icons.tsx';
import styles from './Shell.module.css';

export type TabName = 'home' | 'discover' | 'create' | 'orders' | 'profile';

type PageProps = {
  title: string;
  // A tab's root screen: its title in the header with the tab's icons. Any
  // other screen gets a back header (design: 54 px, back arrow and title).
  tab?: TabName;
  // Replaces the title shown in the header (the document title stays `title`).
  heading?: ReactNode;
  // Where Back goes when there is no history (for example a shared link).
  backTo?: string;
  // Extra header buttons (right side).
  actions?: ReactNode;
  // Screens that bring their own top area (photo, banner) skip the padding.
  flush?: boolean;
  children: ReactNode;
};

function Badge({ count, tone = 'orange' }: { count: number; tone?: 'orange' | 'blue' }) {
  if (!count) return null;
  return <span className={[styles.badge, tone === 'blue' ? styles.badgeBlue : ''].join(' ')} aria-hidden="true">{count > 99 ? '99+' : count}</span>;
}

function HeaderLink({ to, icon, label, count, tone, size = 23 }: { to: string; icon: IconName; label: string; count?: number; tone?: 'orange' | 'blue'; size?: number }) {
  return (
    <NavLink to={to} className={styles.headerButton} aria-label={count ? `${label}, ${count} new` : label}>
      <GIcon name={icon} size={size} />
      <Badge count={count ?? 0} tone={tone} />
    </NavLink>
  );
}

// Icons on a tab's header (design: Home has Messages; Profile has Settings;
// Notifications, Saved and Cart only on phones, where there is no sidebar).
function TabIcons({ tab }: { tab: TabName }) {
  const user = useCurrentUser();
  const counts = useSocialCounts();
  const { count: cartCount } = useCart();
  const { shops } = useAccount();
  const owned = shops.find(shop => shop.role === 'owner') ?? shops[0];
  const dashboard = owned ? `/shop/${encodeURIComponent(owned.shopId)}/dashboard` : null;
  if (!user) return user === null ? <Link to="/auth/sign-in" className={styles.signIn}>Sign in</Link> : null;
  return (
    <div className={styles.headerIcons}>
      {tab === 'home' ? <HeaderLink to="/messages" icon="messages" label="Messages" count={counts.messages} size={25} /> : null}
      {tab === 'profile' && dashboard ? <HeaderLink to={dashboard} icon="dashboard" label="Shop dashboard" size={24} /> : null}
      {tab === 'profile' ? <HeaderLink to="/settings" icon="gear" label="Settings" /> : null}
      <span className={styles.phoneOnly}>
        <HeaderLink to="/notifications" icon="bell" label="Notifications" count={counts.notifications} />
        <HeaderLink to="/saved" icon="saved" label="Saved" size={22} />
        <HeaderLink to="/cart" icon="cart" label="Cart" count={cartCount} tone="blue" size={24} />
      </span>
    </div>
  );
}

export function Page({ title, tab, heading, backTo = '/', actions, flush = false, children }: PageProps) {
  const navigate = useNavigate();
  useEffect(() => {
    document.title = `${title} · Guildite`;
  }, [title]);

  function back() {
    const index = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (index > 0) navigate(-1);
    else navigate(backTo);
  }

  return (
    <div className={styles.screen}>
      {tab ? (
        <header className={styles.tabHeader}>
          <h1 className={tab === 'home' ? styles.homeTitle : styles.tabTitle}>{heading ?? (tab === 'home' ? 'guildite' : title)}</h1>
          <div className={styles.headerIcons}>{actions}<TabIcons tab={tab} /></div>
        </header>
      ) : (
        <header className={styles.subHeader}>
          <button type="button" className={styles.backButton} aria-label="Back" onClick={back}><GIcon name="back" size={22} stroke={2.3} /></button>
          <h1 className={styles.subTitle}>{heading ?? title}</h1>
          {actions ? <div className={styles.headerIcons}>{actions}</div> : null}
        </header>
      )}
      <div className={flush ? styles.flush : styles.page}>{children}</div>
    </div>
  );
}
